/**
 * One field-scoped validation message returned by the API.
 *
 * @example
 * const messages: ActionFieldError[] = [{ key: "email", error: "Required" }];
 */
export type ActionFieldError = {
  error: string;
  key: string;
};

/**
 * Failure of a POST action reduced to the shape every action consumer reads.
 *
 * @example
 * const { status, error } = normalizeActionError(caughtError);
 */
export type NormalizedActionError = {
  status: number;
  error: string;
  errors?: ActionFieldError[];
  data: unknown;
};

/**
 * Reduce any thrown value to a failure payload, including transport errors.
 *
 * @remarks
 * A network failure, an aborted request, or a CORS rejection carries no
 * `response`, so every lookup here is defensive — normalizing must never throw
 * a second error on top of the first, which would leave the caller's loading
 * state stuck.
 *
 * @example
 * try {
 *   await post(path, body);
 * } catch (error) {
 *   return { isOK: false, ...normalizeActionError(error) };
 * }
 */
export function normalizeActionError(error: unknown): NormalizedActionError {
  const errorRecord = asRecord(error);
  const responseRecord = asRecord(errorRecord?.response);
  const responseData = asRecord(responseRecord?.data);

  return {
    status: getStatus(responseRecord?.status ?? errorRecord?.status),
    error: getErrorMessage(responseData, errorRecord),
    errors: getFieldErrors(responseData),
    data: responseRecord?.data,
  };
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object") return undefined;

  return value as Record<string, unknown>;
}

function getStatus(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;

  return value;
}

function getErrorMessage(
  responseData: Record<string, unknown> | undefined,
  errorRecord: Record<string, unknown> | undefined,
): string {
  const candidates = [
    responseData?.error,
    responseData?.message,
    errorRecord?.message,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.length > 0) return candidate;
  }

  return "";
}

function getFieldErrors(
  responseData: Record<string, unknown> | undefined,
): ActionFieldError[] | undefined {
  const candidates = [responseData?.messages, responseData?.errors];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as ActionFieldError[];
  }

  return undefined;
}
